/**
 * Push Client Store — shared state for push-to-Play functionality.
 *
 * Uses protocol DiscoveredDevice type for cross-app compatibility.
 * Used by PushButton component and usePushSender hook in editor-core.
 */

import type { DiscoveredDevice } from "@magic-md/engine-client/protocol";
import { create } from "zustand";

export interface PushClientState {
    /** Whether connected to a Play device */
    connected: boolean;
    /** Whether paired with the Play device */
    paired: boolean;
    /** Whether real-time sync is active */
    syncing: boolean;
    /** Remote Play device info */
    remoteDeviceId: string | null;
    remoteDeviceName: string | null;
    /** Whether the device panel is open */
    panelOpen: boolean;
    /** Whether a pairing challenge has been received (waiting for user to input code) */
    pairingChallengeDevice: string | null;
    /** Whether we're waiting for a pairing result */
    pairingInProgress: boolean;
    /** Last push status message */
    lastPushStatus: "idle" | "sending" | "success" | "failed";
    /** Discovered Play devices from mDNS scan */
    discoveredDevices: DiscoveredDevice[];
    /** Whether we're currently scanning for devices */
    scanning: boolean;
    /** Device ID currently being connected to (for loading state) */
    connectingDeviceId: string | null;

    setPanelOpen: (open: boolean) => void;
    setConnected: (connected: boolean, deviceId?: string | null, deviceName?: string | null) => void;
    setConnectingDeviceId: (id: string | null) => void;
    setPaired: (paired: boolean) => void;
    setSyncing: (syncing: boolean) => void;
    setPairingChallenge: (deviceName: string | null) => void;
    setPairingInProgress: (inProgress: boolean) => void;
    setLastPushStatus: (status: "idle" | "sending" | "success" | "failed") => void;
    setDiscoveredDevices: (devices: DiscoveredDevice[]) => void;
    setScanning: (scanning: boolean) => void;
    reset: () => void;
}

export const usePushClientStore = create<PushClientState>((set) => ({
    connected: false,
    paired: false,
    syncing: false,
    remoteDeviceId: null,
    remoteDeviceName: null,
    panelOpen: false,
    pairingChallengeDevice: null,
    pairingInProgress: false,
    lastPushStatus: "idle",
    discoveredDevices: [],
    scanning: false,
    connectingDeviceId: null,

    setPanelOpen: (open) => set({ panelOpen: open }),
    setConnected: (connected, deviceId, deviceName) =>
        set({
            connected,
            remoteDeviceId: deviceId ?? null,
            remoteDeviceName: deviceName ?? null,
            connectingDeviceId: null,
        }),
    setConnectingDeviceId: (id) => set({ connectingDeviceId: id }),
    setPaired: (paired) => set({ paired }),
    setSyncing: (syncing) => set({ syncing }),
    setPairingChallenge: (deviceName) =>
        set({ pairingChallengeDevice: deviceName }),
    setPairingInProgress: (inProgress) => set({ pairingInProgress: inProgress }),
    setLastPushStatus: (status) => set({ lastPushStatus: status }),
    setDiscoveredDevices: (devices) => set({ discoveredDevices: devices }),
    setScanning: (scanning) => set({ scanning }),
    reset: () =>
        set({
            connected: false,
            paired: false,
            syncing: false,
            remoteDeviceId: null,
            remoteDeviceName: null,
            pairingChallengeDevice: null,
            pairingInProgress: false,
            lastPushStatus: "idle",
            discoveredDevices: [],
            scanning: false,
            connectingDeviceId: null,
        }),
}));
